import React, { type Dispatch, type SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import LockOutlineIcon from 'mdi-react/LockOutlineIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import * as styles from '../offices.styles';
import * as workspaceStyles from '../office-workspace.styles';
import * as surfaceStyles from '../../../core/admin-surface.styles';
import OfficeFormType from '../enums/office-form-types';
import RoomCard from '../cards/room-card.component';
import RoomModal from '../modals/room-modal.component';
import Room from '../models/room/room';
import type Department from '../models/department/department';
import {
  createRoom, deleteRoom, getAllRoomsByOfficeId, updateRoom,
} from '../services/rooms.services';
import { maximumCapacity } from '../models/office.constants';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import { Center } from '../../../core/components/center/center.component';
import notify from '../../../core/components/notify/notify.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import stringIsNullOrWhitespace from '../../../core/utils/validation-utils';
import isThresholdPairValid from '../../../core/utils/threshold-utils';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';

interface Props {
  readonly officeId: string | undefined;
  readonly canEditRoomCapacity: boolean;
  readonly floorPlanEnabled: boolean;
  rooms: Room[];
  setRoomsDispatch: Dispatch<SetStateAction<Room[] | undefined>>;
  /** Used to group and filter the rooms; supplied by the departments card on the same tab. */
  readonly departments?: Department[];
}

const UNASSIGNED_SECTION_KEY = 'unassigned';
const ALL_DEPARTMENTS_KEY = 'all';

interface RoomSection {
  key: string;
  label: string;
  rooms: Room[];
  accent: string;
}

const OfficeRoomsGroup = ({
  officeId,
  canEditRoomCapacity,
  floorPlanEnabled,
  rooms,
  setRoomsDispatch: setRooms,
  departments,
}: Props): React.JSX.Element => {
  const keycloak = useKeycloak();
  const { t } = useTranslation();

  const isMobile = useContext(IsMobileContext);
  const [showRoomModal, setShowRoomModal] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const [newRoom, setNewRoom] = useState<Room>(new Room());
  const [roomsToShow, setRoomsToShow] = useState<Room[] | undefined>(undefined);

  const [query, setQuery] = useState<string>('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');

  // Adopts whatever the hook last fetched, not just the first thing it fetched.
  // The `roomsToShow == null` guard this replaces meant the local copy was
  // taken once and never refreshed, so switching office kept the previous
  // office's rooms on screen even once the new ones had arrived.
  //
  // `rooms` only changes identity when a fetch completes, and a fetch only
  // happens when the office id changes — so optimistic edits made here survive
  // until the user navigates away.
  useEffect(() => {
    if (rooms) {
      setIsLoading(false);
      setRoomsToShow([...rooms]);
    }
  }, [rooms]);

  // With a floorplan, a room's seats are the desks drawn in it, so a new room starts empty and
  // books through the plan; the server creates it that way whatever is sent.
  const capacityFollowsFloorPlan = !canEditRoomCapacity;

  /**
   * Whether this room's capacity is the floorplan's to decide: in a floorplan office, and booked
   * through the plan. A room booked by capacity instead — open seating in a floorplan office —
   * has no desks to count, so its capacity stays a number the administrator sets.
   */
  const capacityFollowsFloorPlanFor = useCallback(
    (room: Room): boolean => capacityFollowsFloorPlan && room.isReservableThroughFloorPlan,
    [capacityFollowsFloorPlan],
  );

  const handleShow = (): void => {
    if (capacityFollowsFloorPlan) {
      setNewRoom({ ...new Room(), capacity: 0, isReservableThroughFloorPlan: true });
    }

    setShowRoomModal(true);
  };
  const handleClose = useCallback(async (): Promise<void> => {
    setNewRoom(new Room());
    setShowRoomModal(false);
  }, [setNewRoom, setShowRoomModal]);

  const handleRoomChange = useCallback(
    (officeFormType: OfficeFormType, value: unknown): void => {
      const roomCopy = { ...newRoom };
      switch (officeFormType) {
        case OfficeFormType.name:
          roomCopy.name = value as string;
          break;
        case OfficeFormType.capacity:
          roomCopy.capacity = value as number;
          break;
        case OfficeFormType.reservableThroughFloorplanEnabled:
          roomCopy.isReservableThroughFloorPlan = value as boolean;
          break;
        case OfficeFormType.roomThresholdMinPercentage:
          roomCopy.thresholdMinPercentage = value as number | undefined;
          break;
        case OfficeFormType.roomThresholdMaxPercentage:
          roomCopy.thresholdMaxPercentage = value as number | undefined;
          break;
        default:
          break;
      }
      setNewRoom(roomCopy);
    },
    [newRoom, setNewRoom],
  );

  const isRoomEmptyOrAlreadyInUse = useCallback(
    (roomToCheck: Room) => {
      if (stringIsNullOrWhitespace(roomToCheck.name)) {
        notify(t('name-empty-message'), false);
        return true;
      }

      if (!isThresholdPairValid(roomToCheck.thresholdMinPercentage, roomToCheck.thresholdMaxPercentage)) {
        notify(t('dashboard-thresholds-validation-message'), false);
        return true;
      }

      // Nothing to check when the capacity is the floorplan's to decide: a new room starts at
      // zero, and an existing one's figure cannot be edited here.
      if (capacityFollowsFloorPlanFor(roomToCheck)) {
        if (roomsToShow?.some((oldRoom) => oldRoom.name === roomToCheck.name && oldRoom.id !== roomToCheck.id)) {
          notify(t('room-already-exists'), false);
          return true;
        }

        return false;
      }

      if (stringIsNullOrWhitespace(roomToCheck.capacity.toString())) {
        notify(t('capacity-empty-message'), false);
        return true;
      }

      if (Number.isNaN(Number(roomToCheck.capacity))) {
        notify(t('capacity-numbers-only-message'), false);
        return true;
      }

      if (roomToCheck.capacity === 0) {
        notify(t('capacity-not-zero-message').replace('[x]', '0'), false);
        return true;
      }

      if (roomToCheck.capacity < 0) {
        notify(t('capacity-negative-message'), false);
        return true;
      }

      if (roomToCheck.capacity > maximumCapacity) {
        notify(t('capacity-exceeds-max-message').replace('[x]', maximumCapacity.toString()));
      }

      if (roomsToShow?.some((oldRoom) => oldRoom.name === roomToCheck.name && oldRoom.id !== roomToCheck.id)) {
        notify(t('room-already-exists'), false);
        return true;
      }
      return false;
    },
    [t, roomsToShow, capacityFollowsFloorPlanFor],
  );

  const handleCreateRoom = useCallback(
    async (roomToAdd: Room): Promise<void> => {
      if (!isRoomEmptyOrAlreadyInUse(roomToAdd)) {
        if (!officeId) {
          return;
        }
        const response = await createRoom(officeId, roomToAdd, keycloak);

        if (response) {
          // Read back rather than assumed: the server decides the capacity — a floorplan office's
          // room starts empty whatever was sent, and the office's setting may not be the one the
          // form is showing yet. The room as sent is only the fallback if the read fails.
          const fetched = await getAllRoomsByOfficeId(officeId, keycloak);
          const roomsCopy = fetched ?? [...(roomsToShow ?? []), { ...roomToAdd, id: response }];

          setRoomsToShow(roomsCopy);
          setRooms(roomsCopy);
          notify(t(capacityFollowsFloorPlan ? 'room-created-floorplan-message' : 'room-saved-success-message'), true);
        } else {
          notify(t('room-saved-error-message'), false);
        }
        handleClose();
      }
    },
    [handleClose, setRooms, isRoomEmptyOrAlreadyInUse, roomsToShow, keycloak, officeId, t, capacityFollowsFloorPlan],
  );

  const handleUpdateRoom = useCallback(
    async (roomToUpdate: Room): Promise<void> => {
      if (!officeId) {
        return;
      }
      if (!isThresholdPairValid(roomToUpdate.thresholdMinPercentage, roomToUpdate.thresholdMaxPercentage)) {
        notify(t('dashboard-thresholds-validation-message'), false);
        return;
      }

      const response = await updateRoom(officeId, roomToUpdate, keycloak);
      if (response != null) {
        const existingRoomToUpdate = roomsToShow?.find((obj) => obj.id === roomToUpdate.id);

        if (existingRoomToUpdate != null) {
          const roomsCopy = roomsToShow != null ? [...roomsToShow] : [];

          roomsCopy[roomsCopy.indexOf(existingRoomToUpdate)] = roomToUpdate;
          setRoomsToShow(roomsCopy);
          setRooms(roomsCopy);
        }
        notify(t('room-saved-success-message'), true);
      } else {
        notify(t('room-saved-error-message'), false);
      }
    },
    [roomsToShow, keycloak, officeId, setRooms, t],
  );

  const shouldDeleteRoom = useCallback(
    async (roomToDelete: Room, acknowledged: boolean): Promise<void> => {
      if (roomToDelete.id == null) {
        notify(t('delete-room-error-message'), false);
        return;
      }

      // deleteRoom reports its own failure through showStatusCodeError — don't toast twice.
      const result = await deleteRoom(roomToDelete.id, keycloak, acknowledged);

      if (!result) {
        return;
      }

      // Push the removal up as well: the parent's list is what feeds the departments card (and the
      // room picker in its edit modal), so keeping only local state leaves a deleted room on screen.
      const remainingRooms = (roomsToShow ?? []).filter((room) => room.id !== roomToDelete.id);
      setRoomsToShow(remainingRooms);
      setRooms(remainingRooms);
      notify(t('delete-room-success-message', { room: roomToDelete.name }), true);
      handleClose();
    },
    [roomsToShow, keycloak, setRooms, handleClose, t],
  );

  // Rooms carry no floor/zone of their own, so departments are the only real grouping the data
  // supports. A room assigned to several departments shows up under each of them.
  const departmentNamesByRoomId = useMemo(() => {
    const map = new Map<string, string[]>();
    (departments ?? []).forEach((department) => {
      department.rooms?.forEach((departmentRoom) => {
        if (!departmentRoom.id) return;
        map.set(departmentRoom.id, [...(map.get(departmentRoom.id) ?? []), department.name]);
      });
    });
    return map;
  }, [departments]);

  const hasUnassignedRooms = useMemo(
    () => (roomsToShow ?? []).some((room) => !departmentNamesByRoomId.has(room.id ?? '')),
    [roomsToShow, departmentNamesByRoomId],
  );

  const departmentFilters = useMemo(() => {
    const filters = [{ key: ALL_DEPARTMENTS_KEY, label: t('all-departments') }];
    (departments ?? []).forEach((department) => {
      if (department.id) {
        filters.push({ key: department.id, label: department.name });
      }
    });
    if (hasUnassignedRooms) {
      filters.push({ key: UNASSIGNED_SECTION_KEY, label: t('no-department') });
    }
    return filters;
  }, [departments, hasUnassignedRooms, t]);

  // A department that gets deleted elsewhere on the tab must not leave the list filtered to a
  // section that no longer exists.
  useEffect(() => {
    if (!departmentFilters.some((filter) => filter.key === departmentFilter)) {
      setDepartmentFilter(ALL_DEPARTMENTS_KEY);
    }
  }, [departmentFilters, departmentFilter]);

  const visibleRooms = useMemo(() => {
    const trimmedQuery = query.trim().toLowerCase();
    return (roomsToShow ?? []).filter((room) => {
      if (trimmedQuery && !room.name.toLowerCase().includes(trimmedQuery)) {
        return false;
      }

      if (departmentFilter === ALL_DEPARTMENTS_KEY) {
        return true;
      }

      if (departmentFilter === UNASSIGNED_SECTION_KEY) {
        return !departmentNamesByRoomId.has(room.id ?? '');
      }

      const department = (departments ?? []).find((candidate) => candidate.id === departmentFilter);
      return department?.rooms?.some((departmentRoom) => departmentRoom.id === room.id) ?? false;
    });
  }, [roomsToShow, query, departmentFilter, departments, departmentNamesByRoomId]);

  const roomSections = useMemo<RoomSection[]>(() => {
    const sections: RoomSection[] = [];

    // The accent is taken from the department's position in the list, so a department shows the
    // same colour here as it does in the departments card next to it.
    (departments ?? []).forEach((department, index) => {
      const sectionRooms = visibleRooms.filter((room) =>
        department.rooms?.some((departmentRoom) => departmentRoom.id === room.id));
      if (sectionRooms.length > 0) {
        sections.push({
          key: department.id ?? department.name,
          label: department.name,
          rooms: sectionRooms,
          accent: workspaceStyles.accentAt(index),
        });
      }
    });

    const unassignedRooms = visibleRooms.filter((room) => !departmentNamesByRoomId.has(room.id ?? ''));
    if (unassignedRooms.length > 0) {
      sections.push({
        key: UNASSIGNED_SECTION_KEY,
        label: t('no-department'),
        rooms: unassignedRooms,
        accent: workspaceStyles.NEUTRAL_ACCENT,
      });
    }

    return sections;
  }, [departments, visibleRooms, departmentNamesByRoomId, t]);

  const capacityScale = useMemo(
    () => (roomsToShow ?? []).reduce((largest, room) => Math.max(largest, room.capacity), 0),
    [roomsToShow],
  );

  // "3 rooms · 24 seats", singular where it applies ("1 room · 1 seat").
  const roomsAndSeatsLabel = (sectionRooms: Room[]): string => {
    const seats = sectionRooms.reduce((total, room) => total + room.capacity, 0);
    const roomWord = sectionRooms.length === 1 ? t('room') : t('rooms');
    const seatWord = seats === 1 ? t('seat') : t('seats');

    return `${sectionRooms.length} ${roomWord} · ${seats} ${seatWord}`;
  };

  const renderRoomCard = (room: Room): React.JSX.Element => (
    <RoomCard
      key={`${room.id ?? ''} ${room.name} ${room.capacity}`}
      initialRoom={room}
      handleUpdateRoom={(updatedRoom): void => {
        handleUpdateRoom(updatedRoom);
      }}
      isEmptyOrInUse={isRoomEmptyOrAlreadyInUse}
      onDelete={(roomToDelete, acknowledged): void => {
        shouldDeleteRoom(roomToDelete, acknowledged).catch(() => undefined);
      }}
      canEditCapacity={canEditRoomCapacity}
      floorplanEnabled={floorPlanEnabled}
      departmentNames={departmentNamesByRoomId.get(room.id ?? '')}
      capacityScale={capacityScale}
    />
  );

  const renderBody = (): React.JSX.Element => {
    if (isLoading) {
      return (
        <Center>
          <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
        </Center>
      );
    }

    if (roomsToShow === undefined || roomsToShow.length === 0) {
      return <span className={style(styles.emptyListMessage)}>{t('no-rooms-available-message')}</span>;
    }

    if (visibleRooms.length === 0) {
      return (
        <div className={style(surfaceStyles.emptyState)}>
          <MagnifyIcon size={28} />
          <strong className={style(surfaceStyles.emptyTitle)}>{t('no-rooms-match-filters')}</strong>
          <span className={style(surfaceStyles.emptyHint)}>{t('no-rooms-match-filters-hint')}</span>
        </div>
      );
    }

    return (
      <>
        {roomSections.map((section) => (
          <section className={style(workspaceStyles.section)} key={section.key}>
            <div className={style(workspaceStyles.sectionHead)}>
              <span className={style(workspaceStyles.sectionPill(section.accent))}>
                {section.label.trim().slice(0, 2).toUpperCase()}
              </span>
              <span className={style(workspaceStyles.sectionTitle)}>{section.label}</span>
              <div className={style(workspaceStyles.sectionRule)} />
              <span className={style(workspaceStyles.sectionMeta)}>{roomsAndSeatsLabel(section.rooms)}</span>
            </div>
            <div className={style(workspaceStyles.roomGrid(isMobile))}>
              {section.rooms.map(renderRoomCard)}
            </div>
          </section>
        ))}
      </>
    );
  };

  return (
    <div className={style(surfaceStyles.card)}>
      <div className={style(surfaceStyles.cardHead(isMobile))}>
        <div>
          <h2 className={style(surfaceStyles.cardTitle)}>{t('rooms-card-title')}</h2>
          <p className={style(surfaceStyles.cardSub)}>{roomsAndSeatsLabel(roomsToShow ?? [])}</p>
        </div>
        <PrimaryButton
          fullWidth={isMobile}
          onClick={handleShow}
          disabled={isLoading}
          className="button_create_room"
        >
          {t('create-room')}
        </PrimaryButton>
      </div>

      {!isLoading && roomsToShow != null && roomsToShow.length > 0 && (
        <div className={style(workspaceStyles.toolbar(isMobile))}>
          <div className={style(workspaceStyles.searchWrap(isMobile))}>
            <span className={style(workspaceStyles.searchIcon)}>
              <MagnifyIcon size={16} />
            </span>
            <input
              type="text"
              maxLength={100}
              className={style(workspaceStyles.searchInput(isMobile))}
              placeholder={t('search-rooms').toString()}
              aria-label={t('search-rooms').toString()}
              value={query}
              onChange={(event): void => setQuery(event.target.value)}
            />
          </div>
          {departmentFilters.length > 1 && (
            <div className={style(workspaceStyles.filterGroup)} role="group" aria-label={t('departments').toString()}>
              {departmentFilters.map((filter, index) => (
                <button
                  key={filter.key}
                  type="button"
                  aria-pressed={departmentFilter === filter.key}
                  className={style(workspaceStyles.filterButton(departmentFilter === filter.key, index === 0))}
                  onClick={(): void => setDepartmentFilter(filter.key)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className={style(workspaceStyles.cardBody)}>
        {renderBody()}
        {!canEditRoomCapacity && (
          <div className={style(workspaceStyles.alert)}>
            <span className={style(workspaceStyles.alertIcon)}>
              <LockOutlineIcon size={18} />
            </span>
            <div>{t('room-not-edditable-warning')}</div>
          </div>
        )}
        <RoomModal
          showModal={showRoomModal}
          handleClose={(): void => {
            handleClose();
          }}
          room={newRoom}
          capacityFollowsFloorPlan={capacityFollowsFloorPlan}
          handleRoomChange={handleRoomChange}
          handleCreateRoom={(room): void => {
            handleCreateRoom(room);
          }}
        />
      </div>
    </div>
  );
};

export default OfficeRoomsGroup;
